'use client'
import { SearchResponse } from '@algolia/client-search'
import { useSearchParams } from 'next/navigation'
import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ALGOLIA_INDEX_PRODUCT } from '@src/config/env'
import {
  Filter,
  SelectedFilter,
  UpdateQueryParams,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { FilterSection } from '@src/quince-components/src'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { useAllQueryState } from '@src/quince-site-common/src/hooks/useQueryState'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import {
  ALGOLIA_CACHE_TIMEOUT,
  algoliaSearchWithFacet,
  getSortByIndex,
} from '@src/quince-site-common/src/services/algoliaService/algolia'
import { AlgoliaSearchResult } from '@src/quince-site-common/src/services/algoliaService/algolia.types'
import { debounce } from '@src/quince-site-common/src/utils/debounce'
import {
  getAlgoliaFilter,
  getSearchResultsCacheKey,
  transformFacetsToFilterOptions,
} from '@src/quince-site-common/src/utils/searchFilter/searchFilterUtils'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import withCache from '@src/quince-site-common/src/utils/withCache'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { pickBy } from '@src/utils/functions/utils'

import { getSelectedFiltersValuesString, setInitialSelectedFilers } from './helpers'
import SearchResults from './searchResults/SearchResults'

const DEBOUNCE_TIME = 300
const ALGOLIA_SEARCH_RESULTS_CACHE_BUCKET_NAME = 'algoliaSearchResultsApiCache'
const ALL_WATCHED_QUERY_KEYS: ('filter' | 'q')[] = ['filter', 'q']

type SearchResultsType = SearchResponse<AlgoliaSearchResult>

const handleSearchQueryChange = (
  searchQuery: string,
  handleAlgoliaSearch: (sq: string, selectedFilters?: SelectedFilter, sortBy?: string) => void,
  selectedFilters?: SelectedFilter,
  sortBy?: string,
) => {
  if (searchQuery) {
    trackEvent(TELEMETRY_EVENT_NAMES.SEARCH, {
      searchQuery: searchQuery.trim(),
    })
  }
  handleAlgoliaSearch(searchQuery, selectedFilters, sortBy)
}

function SearchResultsSection() {
  const [initialFilters, setFilters] = useState<Filter[]>([])
  const [searchResults, setSearchResults] = useState<SearchResultsType>()
  const { current: debouncedHandleSearch } = useRef(debounce(handleSearchQueryChange, DEBOUNCE_TIME))
  const [isFetchingSearchResults, setIsFetchingSearchResults] = useState(false)
  const [isFetchingMoreSearchResults, setIsFetchingMoreSearchResults] = useState(false)
  const defaultSelectedFilters = useMemo(setInitialSelectedFilers, [])
  const [watchedQueryStates, setWatchedQueryStates] = useAllQueryState<'filter' | 'q'>(
    ALL_WATCHED_QUERY_KEYS,
    defaultSelectedFilters as Record<'filter' | 'q', string | undefined>,
    true,
  )

  const hasFilters = useRef<boolean>(false)

  const tracker = 'searchPage'

  const { selectedFilters, searchQuery } = useMemo(() => {
    const selectedFilters = watchedQueryStates.filter
      ? SelectedFilter.getSelectedFilterFromQueryParams(watchedQueryStates.filter)
      : new SelectedFilter({})
    const searchQuery = watchedQueryStates.q ?? ''

    return { selectedFilters, searchQuery }
  }, [watchedQueryStates])

  const previousQueryRef = useRef(searchQuery)
  const totalPagesRef = useRef(2)
  const currentPageRef = useRef(0)

  const handleAlgoliaSearch = useCallback(async (sq: string, selectedFilters?: SelectedFilter, sortBy?: string) => {
    if (currentPageRef.current > 0) {
      setIsFetchingMoreSearchResults(true)
    } else {
      setIsFetchingSearchResults(true)
    }

    try {
      const ALGOLIA_INDEX_KEY = getSortByIndex(sortBy) ?? ALGOLIA_INDEX_PRODUCT // default
      const data = await withCache({
        promiseFn: () => {
          if (sq)
            return algoliaSearchWithFacet(
              sq,
              40,
              ALGOLIA_INDEX_KEY,
              currentPageRef.current,
              getAlgoliaFilter(selectedFilters),
            )
          return Promise.resolve(null)
        },
        cacheBucket: ALGOLIA_SEARCH_RESULTS_CACHE_BUCKET_NAME,
        cacheKey: getSearchResultsCacheKey({
          indexKey: ALGOLIA_INDEX_KEY,
          sq,
          page: currentPageRef.current,
          selectedFilters,
          sortBy,
        }),
        cacheTimeoutInMs: ALGOLIA_CACHE_TIMEOUT,
      })
      if (!data?.hits) return
      setSearchResults(data as SearchResultsType)
      if (!hasFilters.current && sq) {
        if (selectedFilters && SelectedFilter.isAnyFilterSelected(selectedFilters)) {
          void getInitialFilters(sq)
        } else {
          setFilters(transformFacetsToFilterOptions(data.facets ?? {}))
        }
        hasFilters.current = true
      }
      previousQueryRef.current = sq
      currentPageRef.current = currentPageRef.current + 1
      totalPagesRef.current = data.nbPages

      siteStateManager.setSearchAlgoliaQueryId({
        searchQuery: sq,
        searchAlgoliaQueryId: (data as SearchResultsType).queryID ?? '',
      })
    } catch (e: unknown) {
      captureException(e, {
        module: MODULES.ALGOLIA,
        feature: FEATURES.SEARCH_RESULT,
        extraInfo: {
          message:
            e instanceof Error
              ? `Algolia: ${e.name || ''} ${e.message || 'Failed to fetch algolia search results'}`
              : 'Failed to fetch algolia search results',
          searchQuery: sq,
          algoliaIndex: ALGOLIA_INDEX_PRODUCT,
        },
      })
    } finally {
      setIsFetchingMoreSearchResults(false)
    }
  }, [])

  const getInitialFilters = useCallback(async (searchQuery: string) => {
    try {
      const ALGOLIA_INDEX_KEY = getSortByIndex()
      const data = await algoliaSearchWithFacet(
        searchQuery,
        40,
        ALGOLIA_INDEX_KEY,
        currentPageRef.current,
        getAlgoliaFilter(),
      )
      setFilters(transformFacetsToFilterOptions(data.facets ?? {}))
    } catch (e: unknown) {
      captureException(e, {
        module: MODULES.ALGOLIA,
        feature: FEATURES.SEARCH_RESULT,
        extraInfo: {
          message:
            e instanceof Error
              ? `Algolia: ${e.name || ''} ${e.message || 'Failed to fetch algolia search filters'}`
              : 'Failed to fetch algolia search filters',
          searchQuery,
          algoliaIndex: ALGOLIA_INDEX_PRODUCT,
        },
      })
    }
  }, [])

  const resetPageRefs = useCallback(() => {
    totalPagesRef.current = 2
    currentPageRef.current = 0
    previousQueryRef.current = ''
  }, [])

  useEffect(() => {
    resetPageRefs()
    debouncedHandleSearch(searchQuery, handleAlgoliaSearch, selectedFilters)
  }, [resetPageRefs, getSelectedFiltersValuesString(selectedFilters), searchQuery])

  const onLoadMore = useCallback(() => {
    if (currentPageRef.current < totalPagesRef.current) {
      void handleAlgoliaSearch(previousQueryRef.current, selectedFilters)
    }
  }, [handleAlgoliaSearch, selectedFilters])

  function updateQuery({ filter }: UpdateQueryParams) {
    let updatedSelectedFilters = selectedFilters
    if (filter) {
      switch (filter.type) {
        case 'remove': {
          updatedSelectedFilters = SelectedFilter.removeValueFromSelectedFilter(
            selectedFilters,
            filter.value,
            filter.name,
          )
          break
        }
        case 'toggle': {
          updatedSelectedFilters = SelectedFilter.toggleValueFromSelectedFilter(
            selectedFilters,
            filter.name,
            filter.value,
          )
          trackEvent(TELEMETRY_EVENT_NAMES.FILTER_CLICK, {
            label: filter.name,
            value: filter.value,
            context: {
              isSelected: SelectedFilter.isFilterValueSelected(updatedSelectedFilters, filter.name, filter.value),
              selectedFilters: pickBy(updatedSelectedFilters, v => v !== undefined),
            },
          })
          break
        }
        case 'reset': {
          updatedSelectedFilters = new SelectedFilter({})
          break
        }
        default:
          break
      }
    }
    if (updatedSelectedFilters === selectedFilters) return

    setWatchedQueryStates(
      {
        filter: SelectedFilter.getUpdatedSelectedFilterStringForWindowURL(updatedSelectedFilters),
        q: searchQuery,
      },
      null,
    )
  }

  return (
    <div id={'newSearchSection'}>
      <FilterSection
        noOfItems={searchResults?.nbHits ?? 0}
        filters={initialFilters}
        sortBy=""
        selectedFilters={selectedFilters}
        updateQuery={updateQuery}
        showSelectedOption
        shouldRenderColorFilterHtml={false}
        skipSorting
      />

      <SearchResults
        tracker={`${tracker}__search_section`}
        searchQuery={searchQuery || ''}
        searchResults={searchResults}
        isFetchingSearchResults={isFetchingSearchResults}
        isFetchingMoreSearchResults={isFetchingMoreSearchResults}
        setIsFetchingSearchResults={setIsFetchingSearchResults}
        onLoadMore={onLoadMore}
        isFirstPage={currentPageRef.current <= 1}
        hasMorePageToLoad={currentPageRef.current + 1 <= totalPagesRef.current}
      />
    </div>
  )
}

function withSearchQueryRemount(Component: FC<PropsWithChildren>) {
  return function WithSearchQueryRemount(props: PropsWithChildren) {
    const searchParam = useSearchParams()
    const searchQuery = searchParam.get('q')

    return <Component key={searchQuery} {...props} />
  }
}

export default withSearchQueryRemount(SearchResultsSection)
